/**
 * Default: Components / Overwrites / Bootstrap > Modal
 *
 * @copyright 2024 i-fabrik GmbH
 * @author Heiko Pfefferkorn
 */

// import {getTabbableElements} from '@ifabnpm/bootstrap/utils/tabbable';
// import FocusTrap from '@ifabnpm/bootstrap/utils/focustrap';

import SelectorEngine from '@ifabnpm/bootstrap/dom/selector-engine.js';
import EventHandler from '@ifabnpm/bootstrap/dom/event-handler.js';
import Manipulator from '@ifabnpm/bootstrap/dom/manipulator.js';
import Data from '@ifabnpm/bootstrap/dom/data.js';

// -------
// Private
// -------

const NAME      = 'modal';
const DATA_KEY  = `ifab.${NAME}`;
const EVENT_KEY = `.${DATA_KEY}`;

const manipulateTrigger = (elements, flag) => {
	for (const element of elements) {
		if(flag) {
			Manipulator.addClass(element, '_active');
		} else {
			Manipulator.removeClass(element, '_active');
		}
	}
};

const modalIframe = () => {
	const container = SelectorEngine.findOne('#modal-iframe');
	const controls  = (container) ? SelectorEngine.find('[data-modal-trigger="iframe"]') : null;

	if (container) {
		window.pageInstance.modalIframeContainer = container;

		if (!window.pageInstance.modalIframe) {
			const modal     = new bootstrap.Modal(container);
			const modalTitle = SelectorEngine.findOne('[data-modal-title]', container);
			const modalBody = SelectorEngine.findOne('.modal-body', container);

			// Modal wird geöffnet, Daten eintragen.
			container.addEventListener('show.bs.modal', (event) => {
				const trigger   = event.relatedTarget;
				const uri       = Data.get(trigger, `${DATA_KEY}.uri`);
				const context   = Data.get(trigger, `${DATA_KEY}.context`);
				const header    = Data.get(trigger, `${DATA_KEY}.header`);
				const modalType = trigger.dataset.modalType;
				const modalSize = trigger.dataset.modalSize;
				const iframe    = Manipulator.elementPrepend(`<iframe src="${uri}" scrolling="yes"></iframe>`, modalBody);

				container.dataset.modalContext = context;

				if (modalSize !== undefined && modalSize !== '') {
					container.dataset.modalSize = modalSize;
				}

				if (modalType !== undefined && modalType !== '') {
					container.dataset.modalType = modalType;
				}

				Data.set(container, `${DATA_KEY}.iframe`, iframe);
				Data.set(event.target, `${DATA_KEY}.currentTrigger`, trigger);

				Manipulator.addClass(trigger, '_active');

				manipulateTrigger(controls, true);

				if (modalTitle) {
					modalTitle.innerHTML = header;
				}

				window.pageInstance.currentModal = modal;
				window.pageInstance.modalIframeCurrent = iframe;

				// iframe.height = iframe.contentWindow.document.body.scrollHeight;
			});

			// Modal wurde geschlossen, Daten entfernen.
			container.addEventListener('hidden.bs.modal', (event) => {
				const trigger = Data.get(event.target, `${DATA_KEY}.currentTrigger`);
				const iframe  = Data.get(container, `${DATA_KEY}.iframe`);

				if (modalTitle) {
					modalTitle.textContent = '';
				}

				delete container.dataset.modalContext;
				delete container.dataset.modalSize;
				delete container.dataset.modalType;

				iframe.remove();

				window.pageInstance.currentModal = null;
				window.pageInstance.modalIframeCurrent = null;

				manipulateTrigger(controls, false);

				// Set focus back to trigger.
				if (trigger) {
					setTimeout(() => trigger.focus());
				}
			});

			window.pageInstance.modalIframe = modal;
		}
	}

	// Modal trigger holen und initialisieren.
	for (const control of controls) {
		const uri = control.dataset.modalUri;

		if (uri && window.pageInstance.modalIframe) {
			Data.set(control, `${DATA_KEY}.uri`, uri);
			Data.set(control, `${DATA_KEY}.header`, control.dataset.modalHeader?? '');
			Data.set(control, `${DATA_KEY}.context`, control.dataset.modalContext?? 'none');

			EventHandler.on(control, `click${EVENT_KEY}`, (event) => {
				event.preventDefault();

				window.pageInstance.modalIframe.show(event.delegateTarget);
			});

			delete control.dataset.modalContext;
			delete control.dataset.modalHeader;
			delete control.dataset.modalUri;
		} else {
			Manipulator.setDisabled(control);
		}
	}
};

// ------
// Public
// ------

/**
 * ´Modal´-Elemente initialisieren.
 *
 * @returns {HTMLElement|Array}
 */
const init = () => {
	const pageIsModal = (Manipulator.getDataAttribute(document.body, 'page-type') === 'modal');

	// Ist der aktuelle Kontext des Dokumentes ´modal´ (also ein Iframe), dann muss die aktuelle Höhe des Dokumentes an
	// das aktive Modal in `window.parent` übergeben werden.
	if (pageIsModal) {
		const currentModalIframe = window.parent.pageInstance.modalIframeCurrent;
		const container    = SelectorEngine.findOne('.page-main');

		if (currentModalIframe) {
			const updateHeight = () => {
				setTimeout(() => {
					currentModalIframe.style.setProperty(`--_modal-iframe-doc-scroll-height`, `${document.body.scrollHeight}px`);
				}, 50);
			};

			const modalResizeObserver = new ResizeObserver(entries => {
				if (entries[0].target) {
					updateHeight();
				}
			});

			modalResizeObserver.observe(container);

			updateHeight();
		}
	} else {
		modalIframe();
	}

	// Close modal trigger
	const closeModal = SelectorEngine.find('[data-close="modal"]');

	for (const element of closeModal) {
		if (window.pageInstance.currentModal || window.parent.pageInstance.currentModal) {
			EventHandler.on(element, 'click', (event) => {
				event.preventDefault();
				event.stopPropagation();

				const modal = window.parent.pageInstance.currentModal ?? window.pageInstance.currentModal;

				modal.hide();
			})
		} else {
			Manipulator.setDisabled(element);
		}
	}
};

// Export
export default init;
